import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

// Components
import { Popups } from 'components';

// Constants
import { Button, Link } from '@beef/ui-kit';
import { ymPushParams } from 'utils/analytics/ym';
import { ADD_FAMILY_SUCCESS } from 'utils/analytics/constants';
import { PAGE_PATHS, POPUP_NAMES } from '../../constants';

// Utils
import mock from './mock';
import {
  changeInviteIsProcessing,
  changeInviteItems,
  deleteOrCancelChosenCtn,
  saveNewChildLimits,
  setActiveStep,
  setPopupStatus,
} from '../../store/actions';
import { maskPhoneNumber } from '../../../../utils/validators';
import { replaceSubstring } from '../../utils';

const { ResultPopup } = Popups;

const CommonResultPopup = ({ step, invitingNumber, inProgress }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { pathname } = useLocation();
  const chosenCtn = (pathname.match(/\d{10}/gi) || []).join();

  /** Удаление номера или отмена приглашения в Семью */
  const handleDeleteOrCancelInvitation = useCallback(() => {
    dispatch(deleteOrCancelChosenCtn(chosenCtn));
  }, [chosenCtn]);

  /** Закрытие попапа */
  const handleCloseModal = useCallback(() => {
    dispatch(setPopupStatus(false));
  }, []);

  /** Закрытие попап и редирект на страницу настроек */
  const handleCloseAndRedirect = useCallback(() => {
    handleCloseModal();
    history.push(PAGE_PATHS.information);
  }, []);

  /** Успешное удаление номера или отмена приглашения */
  const handleDeleteOrCancelSuccess = useCallback(() => {
    handleCloseAndRedirect();
    dispatch(changeInviteItems(chosenCtn));
  }, [chosenCtn, handleCloseAndRedirect]);

  /** Успешное сохранение новых лимитов для ребенка */
  const handleLimitsSavedSuccess = useCallback(() => {
    handleCloseAndRedirect();
    dispatch(changeInviteIsProcessing(chosenCtn));
  }, [chosenCtn, handleCloseAndRedirect]);

  /** Сохранение новых лимитов */
  const handleSaveNewChildLimits = useCallback(() => {
    dispatch(saveNewChildLimits());
  }, []);

  const resolveActions = {
    addedSuccessfully: handleCloseModal,
    error: handleCloseModal,
    deleteSuccess: handleDeleteOrCancelSuccess,
    delete: handleDeleteOrCancelInvitation,
    cancelInvitation: handleDeleteOrCancelInvitation,
    saveLimits: handleSaveNewChildLimits,
    limitsSavedSuccess: handleLimitsSavedSuccess,
  };

  const currentPopup = mock[step] || {};
  const {
    arrowText, imageUrl, title, description, buttonGroup,
  } = currentPopup;

  if (step === POPUP_NAMES.addedSuccessfully) {
    // eslint-disable-next-line camelcase
    ymPushParams({ lk_ev_: ADD_FAMILY_SUCCESS });
  }

  const maskedCellNumber = () => {
    let target = chosenCtn;
    if (step === POPUP_NAMES.addedSuccessfully) {
      target = invitingNumber;
    }
    return maskPhoneNumber(target);
  };

  const pasteCtnToString = (string) => replaceSubstring(string, '{{CELLNUMBER}}', maskedCellNumber());
  const pasteCtnToTitle = pasteCtnToString(title);
  const pasteCtnToDescription = pasteCtnToString(description);

  /** Устанавливаем функцию для стрелки возврата к попапу добавления номера */
  const setOnBach = () => {
    if (step === POPUP_NAMES.notFoundError || step === POPUP_NAMES.diffRegionError) {
      return () => dispatch(setActiveStep(POPUP_NAMES.add));
    }
    return null;
  };

  return (
    <ResultPopup
      hasButtons
      arrowText={arrowText}
      imageUrl={imageUrl}
      title={pasteCtnToTitle}
      description={pasteCtnToDescription}
      onReturnBackBtnClick={setOnBach()}
      onClick={resolveActions[step]}
      buttonUrl={buttonGroup[0].buttonUrl}
      buttonText={buttonGroup[0].buttonText}
      inProgress={inProgress}
    >
      {buttonGroup[1] && (
        <>
          {!buttonGroup[1].buttonUrl && (
            <Button
              variant="tertiary"
              size="m"
              fullWidth
              onClick={handleCloseModal}
              disabled={inProgress}
            >
              {buttonGroup[1].buttonText}
            </Button>
          )}
          {buttonGroup[1].buttonUrl && (
            <Link
              href={buttonGroup[1].buttonUrl}
              buttonSize="m"
              variant="button"
              buttonVariant="tertiary"
              isFullWidth
            >
              {buttonGroup[1].buttonText}
            </Link>
          )}
        </>
      )}
    </ResultPopup>
  );
};

CommonResultPopup.propTypes = {
  step: PropTypes.string,
  invitingNumber: PropTypes.string,
  inProgress: PropTypes.bool,
};

export { CommonResultPopup };
